import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toSnakeCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const isSchemaError = (error) => {
  return error?.code === '42P01' || error?.message?.includes('does not exist');
};

export const supervisorApprovalService = {
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('supervisor_approvals')
        ?.select(`
          *,
          supervisor_profile:user_profiles!supervisor_approvals_supervisor_id_fkey(
            id,
            full_name,
            email,
            role
          ),
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot
          )
        `)
        ?.eq('work_order_id', workOrderId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching supervisor approvals:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch supervisor approvals:', error);
      throw error;
    }
  },

  async create(approvalData) {
    try {
      const snakeCaseData = toSnakeCase(approvalData);
      const { data, error } = await supabase
        ?.from('supervisor_approvals')
        ?.insert(snakeCaseData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating supervisor approval:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create supervisor approval:', error);
      throw error;
    }
  },

  async update(approvalId, updateData) {
    try {
      const snakeCaseData = toSnakeCase(updateData);
      const { data, error } = await supabase
        ?.from('supervisor_approvals')
        ?.update(snakeCaseData)
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating supervisor approval:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update supervisor approval:', error);
      throw error;
    }
  },

  async approve(approvalId, approvalNotes, signatureData) {
    try {
      const updateData = {
        status: 'approved',
        approval_notes: approvalNotes,
        signature_data: signatureData,
        reviewed_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('supervisor_approvals')
        ?.update(updateData)
        ?.eq('id', approvalId)
        ?.select(`
          *,
          work_order:work_orders(
            id,
            folio_ot
          ),
          supervisor_profile:user_profiles!supervisor_approvals_supervisor_id_fkey(
            id,
            full_name
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error approving:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to approve:', error);
      throw error;
    }
  },

  async uploadPhoto(file, workOrderId, photoType) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${workOrderId}/${photoType}_${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data, error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading photo:', error?.message);
        throw error;
      }

      return data?.path;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      throw error;
    }
  },

  async getPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error getting photo URL:', error?.message);
        throw error;
      }

      return data?.signedUrl;
    } catch (error) {
      console.error('Failed to get photo URL:', error);
      throw error;
    }
  },

  async deletePhoto(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.remove([filePath]);

      if (error) {
        console.error('Error deleting photo:', error?.message);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete photo:', error);
      throw error;
    }
  }
};